import { Country, IdentityCheckStatus } from '@afterpay/types'
import { Route } from '../../config/router'
import { getIdentityVerificationFailedRoute } from '../../model'
import {
  fetchCreditLimit,
  setLoggedInLoadingMessageKey,
  showToast,
  useAppDispatch,
  useAppRouter,
  useConsumerCountry,
  useIdentityVerifyMutation,
  useLazyGetCreditLimitQuery,
} from '../../state'
import { Channel } from '@afterpaytouch/portal-api/types'
import { useAmplitudeWithEnduringEventProperties } from '../../integrations/amplitude'
import { TrackingEvent } from '../../model/amplitude'
import { LaunchDarklyFlags, useFlag } from '../useFlag'
import { useRegulatoryFlow } from '../useRegulatoryFlow/useRegulatoryFlow'
import { useUkRegFlags } from '../useUkRegFlags/useUkRegFlags'
import { extractApiError } from '../extractApiError'

interface HandleIdentityVerifyOutcomeOptions {
  status: IdentityCheckStatus
  showToastBody: boolean
}
interface VerifyAndHandleOptions {
  showToastBody: boolean
}
export const useIdentityVerifyRedirect = (): {
  verifyAndHandleOutcome: (opts: VerifyAndHandleOptions) => Promise<IdentityCheckStatus>
} => {
  const dispatch = useAppDispatch()
  const router = useAppRouter()
  const [identityVerify] = useIdentityVerifyMutation()
  const { logEvent } = useAmplitudeWithEnduringEventProperties()
  const completeUnsuitabilityOnHomepage = useFlag(LaunchDarklyFlags.CompleteAuUnsuitabilityOnHomepage)
  const { isGbRegFlow } = useUkRegFlags()
  const { handleRegulatoryFlow } = useRegulatoryFlow()
  const country = useConsumerCountry()
  const manualIdvEnabled = useFlag(LaunchDarklyFlags.ManualIdvEnabled)
  const useRtkQuery = useFlag(LaunchDarklyFlags.PortalRtkQueryMigration)
  const [triggerGetCreditLimit] = useLazyGetCreditLimitQuery()

  const handleIdentityVerifyOutcome = async ({ status, showToastBody }: HandleIdentityVerifyOutcomeOptions): Promise<void> => {
    switch (status) {
      case IdentityCheckStatus.VERIFIED: {
        logEvent(TrackingEvent.ACCOUNT_VERIFIED)
        dispatch(showToast({ toastType: 'accountVerified', showToastBody }))
        const isAuRegFlow = completeUnsuitabilityOnHomepage && country === Country.AU
        if (isAuRegFlow || isGbRegFlow) {
          try {
            if (useRtkQuery) {
              await triggerGetCreditLimit().unwrap()
            } else {
              await dispatch(fetchCreditLimit()).unwrap()
            }
            logEvent(TrackingEvent.FETCH_CREDIT_LIMIT_ON_LOGGED_IN_PAGE)
            router.replace(Route.HOME)
          } catch (creditLimitError: unknown) {
            const apiError = extractApiError(useRtkQuery, creditLimitError)
            if (!handleRegulatoryFlow(apiError)) router.replace(Route.HOME)
          }
        } else {
          router.replace(Route.LOGGED_IN)
        }
        break
      }
      case IdentityCheckStatus.FAILED: {
        router.push(getIdentityVerificationFailedRoute(manualIdvEnabled))
        break
      }
      default: {
        dispatch(setLoggedInLoadingMessageKey('verifyIdentity'))
        router.replace(Route.LOGGED_IN)
        break
      }
    }
  }

  const verifyAndHandleOutcome = async ({ showToastBody }: VerifyAndHandleOptions): Promise<IdentityCheckStatus> => {
    const { identityCheckStatus } = await identityVerify({ channel: Channel.PORTAL }).unwrap()
    await handleIdentityVerifyOutcome({ status: identityCheckStatus, showToastBody })
    return identityCheckStatus
  }

  return { verifyAndHandleOutcome }
}
